import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import logoOptimized from '@/assets/logo-optimized.webp';

// Primary nav - trust pages first
const primaryNavLinks = [
  { label: 'Home', href: '/' },
  { label: 'Identity Pixel', href: '/v4' },
  { label: 'Data Sources', href: '/where-the-data-comes-from' },
  { label: 'Methodology', href: '/how-intent-data-is-gathered' },
  { label: "Why It's Accurate", href: '/why-our-data-is-accurate' },
];

// Secondary nav
const secondaryNavLinks = [
  { label: 'Solutions', href: '/7-core-segments' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

const desktopLinkClasses = "relative px-3 py-1.5 rounded-md text-foreground hover:text-primary hover:bg-primary/10 transition-all font-medium text-lg after:content-[''] after:absolute after:w-[calc(100%-1.5rem)] after:scale-x-0 after:h-0.5 after:bottom-1 after:left-3 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left";

const mobileLinkClasses = "relative block px-3 py-2 rounded-md text-foreground hover:text-primary hover:bg-primary/10 transition-all font-medium after:content-[''] after:absolute after:w-[calc(100%-1.5rem)] after:scale-x-0 after:h-0.5 after:bottom-1.5 after:left-3 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md shadow-sm border-b border-border/50' 
          : 'bg-background/80 backdrop-blur-sm'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center group">
            <img
              src={logoOptimized}
              alt="Data Aviator"
              width="160"
              height="64"
              className="h-12 sm:h-14 lg:h-16 w-auto shrink-0 block transition-transform duration-300 group-hover:scale-105"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-7">
            {/* Primary nav links */}
            {primaryNavLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(desktopLinkClasses, isActive && 'text-primary after:scale-x-100')}
                >
                  {link.label}
                </Link>
              );
            })}
            
            {/* Separator */}
            <div className="w-px h-7 bg-border" aria-hidden="true" />
            
            {/* Secondary nav links */}
            {secondaryNavLinks.map((link) => {
              const isActive = pathname === link.href;
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(desktopLinkClasses, isActive && 'text-primary after:scale-x-100')}
                >
                  {link.label}
                </Link>
              );
            })}

            <a
              href="https://calendly.com/dataaviator/15-min-demo?utm_source=site&utm_content=nav"
              target="_blank"
              rel="noopener"
              className="ml-2 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              Book a Walkthrough
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            id="mobile-menu-toggle"
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile navigation menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden py-4 border-t border-border/50 bg-background/95 backdrop-blur-md animate-fade-in"
          >
            <div className="flex flex-col gap-1">
              {/* Primary links */}
              <div className="pb-2 mb-2 border-b border-border/30">
                {primaryNavLinks.map((link) => {
                  const isActive = pathname === link.href;
                  return (
                    <Link
                      key={link.label}
                      to={link.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(mobileLinkClasses, isActive && 'text-primary after:scale-x-100')}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
              
              {/* Secondary links */}
              {secondaryNavLinks.map((link) => {
                const isActive = pathname === link.href;
                return (
                  <Link
                    key={link.label}
                    to={link.href}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(mobileLinkClasses, isActive && 'text-primary after:scale-x-100')}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}

              <a
                href="https://calendly.com/dataaviator/15-min-demo?utm_source=site&utm_content=nav"
                target="_blank"
                rel="noopener"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-3 mx-3 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2.5 text-base font-semibold text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                Book a Walkthrough
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
